import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly message: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly intent?: 'danger' | 'warning' | 'neutral';
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * Accessible confirmation modal that replaces `window.confirm`.
 *
 * - Renders nothing when `open` is false.
 * - Traps focus to the dialog while open.
 * - `Escape` triggers `onCancel`.
 * - Wires `aria-modal`, `role="dialog"`, and `aria-labelledby`.
 */
export const ConfirmDialog = ({
  open,
  message,
  confirmLabel,
  cancelLabel,
  intent = 'neutral',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => {
  const { t } = useTranslation();
  const titleId = 'confirm-dialog-title';
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onCancel]);

  // Focus cancel button when dialog opens
  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const resolvedConfirmLabel = confirmLabel ?? t('confirm');
  const resolvedCancelLabel  = cancelLabel  ?? t('cancel');

  const confirmButtonClass =
    intent === 'danger'
      ? 'bg-red-600 hover:bg-red-700 text-white'
      : intent === 'warning'
      ? 'bg-amber-500 hover:bg-amber-600 text-white'
      : 'bg-primary-600 hover:bg-primary-700 text-white';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      aria-hidden="false"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-xl shadow-xl w-full max-w-sm"
      >
        {/* Message */}
        <div className="px-6 py-5">
          <p
            id={titleId}
            className="text-base font-medium text-gray-900"
          >
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 px-6 pb-5">
          <button
            ref={cancelRef}
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {resolvedCancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${confirmButtonClass}`}
          >
            {resolvedConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
